import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import type { EvidenciaAnexo, LancamentoConta, WorkflowConciliacao } from './models';
import workflowService from './workflowService';

const money = (v: number | null | undefined) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v ?? 0);

const TipoBadge: React.FC<{ tipo: 'CREDITO' | 'DEBITO' }> = ({ tipo }) => (
  <span className={`wf-capa-tipo wf-capa-tipo--${tipo.toLowerCase()}`}>{tipo === 'CREDITO' ? 'C' : 'D'}</span>
);

const LancamentoRow: React.FC<{ item: LancamentoConta }> = ({ item }) => (
  <div className='wf-capa-lancamento'>
    <div><strong>{money(item.valor)}</strong><TipoBadge tipo={item.tipo} /></div>
    <p>{item.historico}</p>
    <span>{item.codigoContabil} • {item.data}</span>
  </div>
);

// ─── Banner de Divergência ────────────────────────────────────────────────────

const BannerDivergencia: React.FC<{ item: WorkflowConciliacao }> = ({ item }) => {
  const { statusDivergencia, divergencia } = item;

  if (statusDivergencia === 'CONCILIADO') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#f0fff4', border: '1.5px solid #50cd89', borderRadius: 10, padding: '12px 18px', marginBottom: 16 }}>
        <span style={{ fontSize: 22 }}>✅</span>
        <div>
          <strong style={{ fontSize: 14, color: '#00a86b' }}>Conciliado Automaticamente</strong>
          <div style={{ fontSize: 12, color: '#5e6278', marginTop: 2 }}>
            Saldo Contábil e Saldo Suporte estão em conformidade. Divergência = R$ 0,00.
          </div>
        </div>
      </div>
    );
  }

  if (statusDivergencia === 'JUSTIFICADO') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#fffbea', border: '1.5px solid #ffc700', borderRadius: 10, padding: '12px 18px', marginBottom: 16 }}>
        <span style={{ fontSize: 22 }}>⚠️</span>
        <div>
          <strong style={{ fontSize: 14, color: '#b37400' }}>Divergência Justificada — Aguardando Aprovação</strong>
          <div style={{ fontSize: 12, color: '#5e6278', marginTop: 2 }}>
            Diferença de {money(divergencia)} justificada tecnicamente. Processo liberado para aprovação.
          </div>
        </div>
      </div>
    );
  }

  // DIVERGENCIA_DETECTADA
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#fff1f2', border: '1.5px solid #f64e60', borderRadius: 10, padding: '12px 18px', marginBottom: 16 }}>
      <span style={{ fontSize: 22 }}>🚨</span>
      <div>
        <strong style={{ fontSize: 14, color: '#f64e60' }}>Divergência Detectada — Justificativa Obrigatória</strong>
        <div style={{ fontSize: 12, color: '#5e6278', marginTop: 2 }}>
          Diferença de <strong>{money(divergencia)}</strong> entre o Saldo Contábil e o Saldo Suporte.
          Preencha a justificativa técnica abaixo para liberar o envio para aprovação.
        </div>
      </div>
    </div>
  );
};

// ─── Painel de Dados do Motor ────────────────────────────────────────────────

const PainelMotor: React.FC<{ item: WorkflowConciliacao }> = ({ item }) => {
  const div = item.divergencia ?? 0;
  const corDiv = div === 0 ? '#50cd89' : '#f64e60';

  return (
    <div className='wf-capa-block' style={{ border: '1.5px solid #009ef7', borderRadius: 10, overflow: 'hidden' }}>
      <div className='wf-capa-block__title' style={{ background: '#009ef7', color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>🔗</span> Dados do Motor de Conciliação <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.85 }}>(somente leitura)</span>
      </div>
      <div style={{ padding: '16px 20px' }}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 140px', background: '#f8f9fa', borderRadius: 8, padding: '12px 16px', borderLeft: '4px solid #009ef7' }}>
            <div style={{ fontSize: 11, color: '#7e8299', marginBottom: 4 }}>Saldo Contábil</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#181c32' }}>{money(item.saldoContabil)}</div>
            <div style={{ fontSize: 10, color: '#a1a5b7', marginTop: 2 }}>Valor apurado na contabilidade</div>
          </div>
          <div style={{ flex: '1 1 140px', background: '#f8f9fa', borderRadius: 8, padding: '12px 16px', borderLeft: '4px solid #7239ea' }}>
            <div style={{ fontSize: 11, color: '#7e8299', marginBottom: 4 }}>Saldo Suporte</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#181c32' }}>{money(item.saldoSuporte)}</div>
            <div style={{ fontSize: 10, color: '#a1a5b7', marginTop: 2 }}>Valor apurado no sistema suporte/ERP</div>
          </div>
          <div style={{ flex: '1 1 140px', background: div === 0 ? '#f0fff4' : '#fff1f2', borderRadius: 8, padding: '12px 16px', borderLeft: `4px solid ${corDiv}` }}>
            <div style={{ fontSize: 11, color: '#7e8299', marginBottom: 4 }}>Diferença</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: corDiv }}>{money(div)}</div>
            <div style={{ fontSize: 10, color: '#a1a5b7', marginTop: 2 }}>
              {div === 0 ? 'Sem divergência' : 'Saldo Contábil − Saldo Suporte'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── CapaContaContabil ────────────────────────────────────────────────────────

interface RouteParams { id: string; }

const CapaContaContabil: React.FC = () => {
  const { id } = useParams<RouteParams>();
  const history = useHistory();
  const [generating, setGenerating] = useState(false);
  const [justificativa, setJustificativa] = useState('');
  const [justificativaOriginal, setJustificativaOriginal] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [evidencias, setEvidencias] = useState<EvidenciaAnexo[]>([]);
  const [novaEvidencia, setNovaEvidencia] = useState('');
  const [item, setItem] = useState<WorkflowConciliacao | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    workflowService.obterConciliacao(Number(id))
      .then(data => {
        if (data) {
          setItem(data);
          const jt = data.justificativaTecnica ?? '';
          setJustificativa(jt);
          setJustificativaOriginal(jt);
          const raw = data as any;
          if (Array.isArray(raw.evidencias)) {
            setEvidencias(raw.evidencias.map((e: any): EvidenciaAnexo => ({
              id: e.evidenciaId,
              nome: e.nomeArquivo,
              tamanho: e.tamanhoBytes ? `${(e.tamanhoBytes / 1024).toFixed(0)} KB` : '—',
              tipo: e.tipoMime?.includes('pdf') ? 'PDF' : e.tipoMime?.includes('sheet') ? 'Excel' : 'DOC',
              dataUpload: e.enviadoEm ? new Date(e.enviadoEm).toLocaleString('pt-BR') : '—',
              usuario: e.enviadoPorNome ?? '—',
            })));
          }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className='wf-page'><p style={{ padding: 32, color: '#a1a5b7' }}>Carregando...</p></div>;
  if (!item) return <div className='wf-page'><div className='wf-header'><div><h1>Capa não encontrada</h1><p>Conciliação não encontrada.</p></div></div></div>;

  const lancamentos: LancamentoConta[] = [];
  const ajustes: LancamentoConta[] = [];
  const composicao: LancamentoConta[] = [];
  const isAtivo = item.conta.startsWith('1.');
  const tipoConta = isAtivo ? 'Ativo' : item.conta.startsWith('2.') ? 'Passivo' : 'Resultado';
  const somaAjustes = ajustes.reduce((acc, l) => acc + l.valor, 0);
  const somaComposicao = composicao.reduce((acc, l) => acc + l.valor, 0);
  const isFinalizada = item.status === 'APROVADA';

  // Lógica de trava do botão "Enviar para Aprovação"
  const justificativaAtual = justificativa.trim();
  const divergenciaResolvida = item.statusDivergencia === 'CONCILIADO'
    || (item.statusDivergencia === 'DIVERGENCIA_DETECTADA' && justificativaAtual.length > 0)
    || item.statusDivergencia === 'JUSTIFICADO';
  const podeEnviarAprovacao = item.podeEnviarAprovacao || divergenciaResolvida;

  const statusCapa = isFinalizada ? 'Finalizada' : item.status === 'AGUARDANDO_APROVACAO' || item.status === 'EM_ANALISE' ? 'Aguardando Aprovação' : item.status === 'DIVERGENCIA_DETECTADA' ? 'Divergência Detectada' : item.status === 'CONCILIADO_AUTOMATICAMENTE' ? 'Conciliado Automaticamente' : 'Em Edição';
  const statusCapaClass = isFinalizada ? 'wf-badge--aprovada' : item.status === 'AGUARDANDO_APROVACAO' ? 'wf-badge--aguardando_aprovacao' : item.status === 'DIVERGENCIA_DETECTADA' ? 'wf-badge--rejeitada' : item.status === 'CONCILIADO_AUTOMATICAMENTE' ? 'wf-badge--concluida' : 'wf-badge--em_preenchimento';

  const gerarPdf = async () => {
    setGenerating(true);
    try {
      const blob = await workflowService.exportarConciliacao(item.id, 'pdf');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `capa_conciliacao_${item.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* silent */ }
    setGenerating(false);
  };

  const exportarExcel = async () => {
    try {
      const blob = await workflowService.exportarConciliacao(item.id, 'excel');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `capa_conciliacao_${item.id}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* silent */ }
  };

  const salvarJustificativa = async () => {
    if (!justificativa.trim()) return;
    setSalvando(true);
    try {
      await workflowService.salvarJustificativa(item.id, justificativa);
      setJustificativaOriginal(justificativa);
      // Recarregar para atualizar statusDivergencia e podeEnviarAprovacao
      const updated = await workflowService.obterConciliacao(item.id);
      if (updated) setItem(updated);
      Swal.fire({ icon: 'success', title: 'Justificativa salva!', text: 'A justificativa técnica foi registrada com trilha SOX.', timer: 3000, showConfirmButton: false, toast: true, position: 'top-end' });
    } catch {
      Swal.fire({ icon: 'error', title: 'Erro ao salvar', text: 'Tente novamente.', confirmButtonText: 'OK' });
    } finally {
      setSalvando(false);
    }
  };

  const adicionarEvidencia = () => {
    if (!novaEvidencia.trim()) return;
    setEvidencias(prev => [...prev, { id: Date.now(), nome: novaEvidencia, tamanho: '—', tipo: 'DOC', dataUpload: new Date().toLocaleString('pt-BR'), usuario: 'Usuário atual' }]);
    setNovaEvidencia('');
  };

  const justificativaAlterada = justificativa.trim() !== justificativaOriginal.trim();

  return <div className='wf-page'>
    <div className='wf-breadcrumb'>Home &rsaquo; Motor de Conciliação &rsaquo; {item.conta} &rsaquo; Capa de Conciliação</div>
    <div className='wf-header'>
      <div>
        <h1>Capa da Conta Contábil</h1>
        <p>{item.empresa} • {item.conta} — {item.descricaoConta} • {item.periodo} &nbsp;<span className={`wf-badge ${statusCapaClass}`}>{statusCapa}</span></p>
      </div>
      <div className='wf-header__actions'>
        <button className='wf-btn wf-btn--secondary' onClick={() => history.push(`/workflow-aprovacao/conciliacoes/${item.id}`)}>Voltar à conciliação</button>
        {isFinalizada && <>
          <button className='wf-btn wf-btn--secondary' onClick={exportarExcel}>Exportar Excel</button>
          <button className='wf-btn wf-btn--danger' disabled={generating} onClick={gerarPdf}>
            {generating ? 'Gerando PDF...' : <><i className='fa fa-file-pdf' style={{ marginRight: 6 }} />Gerar PDF</>}
          </button>
        </>}
      </div>
    </div>

    {!isFinalizada && <div className='wf-message'>O botão "Gerar PDF" e "Exportar Excel" ficam disponíveis apenas após a conciliação ser <strong>Aprovada</strong> (Etapa 6 — Encerramento concluída).</div>}

    {/* Banner de status de divergência */}
    <BannerDivergencia item={item} />

    <div className='wf-capa'>
      {/* Cabeçalho */}
      <div className='wf-capa-block'>
        <div className='wf-capa-block__title'>Capa de Conciliação</div>
        <div className='wf-capa-grid'>
          <div><span>Conta</span><strong>{item.conta} — {item.descricaoConta}</strong></div>
          <div><span>Empresa</span><strong>{item.empresa} ({item.cnpj})</strong></div>
          <div><span>Período</span><strong>{item.periodo}</strong></div>
          <div><span>Tipo</span><strong>{tipoConta}</strong></div>
          <div><span>Responsável atual</span><strong>{item.responsavelAtual}</strong></div>
          <div><span>Hash de integridade</span><strong><code>{item.hashIntegridade}</code></strong></div>
        </div>
      </div>

      {/* Dados do Motor de Conciliação (read-only) */}
      <PainelMotor item={item} />

      {/* Demonstrativo */}
      <div className='wf-capa-block'>
        <div className='wf-capa-block__title wf-capa-block__title--success'>Demonstrativo do Saldo</div>
        <div className='wf-capa-saldo'>
          <div><span>Saldo Contábil</span><strong>{money(item.saldoContabil)}</strong></div>
          <div><span>Ajustes e Pendências (Créditos)</span><strong>{money(somaAjustes)}</strong></div>
          <div><span>Composição (Débitos)</span><strong>{money(somaComposicao)}</strong></div>
          <div className={item.divergencia ? 'is-danger' : ''}><span>Saldo Conciliado / Divergência</span><strong>{money(item.saldoConciliado)}{item.divergencia ? ` (div. ${money(item.divergencia)})` : ''}</strong></div>
        </div>
      </div>

      {/* Justificativa Técnica */}
      <div className='wf-capa-block'>
        <div className='wf-capa-block__title' style={{ background: item.justificativaObrigatoria ? '#f64e60' : '#8950fc', display: 'flex', alignItems: 'center', gap: 8 }}>
          {item.justificativaObrigatoria && <span>⚠️</span>}
          Justificativa Técnica — Editor de Conteúdo
          {item.justificativaObrigatoria && <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.9 }}>(obrigatória para liberar aprovação)</span>}
        </div>
        <div style={{ padding: 16 }}>
          {item.justificativaObrigatoria && !justificativa.trim() && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: '#fff1f2', border: '1px solid #f64e60', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: '#f64e60' }}>
              <span>🚫</span>
              <span>Esta conciliação possui <strong>divergência de {money(item.divergencia)}</strong> entre Saldo Contábil e Saldo Suporte. Descreva a justificativa técnica para liberar o envio para aprovação (ex: depreciação, mútuo, variações financeiras).</span>
            </div>
          )}
          <p style={{ fontSize: 11, color: '#a1a5b7', marginBottom: 10 }}>
            Campo de descrição técnica da conciliação. Salvo diretamente no processo com trilha de auditoria SOX.
            {item.justificativaObrigatoria && <strong style={{ color: '#f64e60' }}> * Campo obrigatório.</strong>}
          </p>
          <textarea
            rows={5}
            value={justificativa}
            onChange={e => setJustificativa(e.target.value)}
            style={{
              width: '100%', padding: '10px 12px', borderRadius: 8, fontSize: 12, fontFamily: 'inherit',
              border: `1.5px solid ${item.justificativaObrigatoria && !justificativa.trim() ? '#f64e60' : '#dfe3ea'}`,
              outline: 'none', resize: 'vertical'
            }}
            disabled={isFinalizada}
            placeholder={item.justificativaObrigatoria
              ? 'Descreva a causa da divergência: depreciação contábil, mútuo entre empresas, variações financeiras, diferenças de competência...'
              : 'Descreva tecnicamente a conta, os critérios de conciliação utilizados e os ajustes identificados...'}
          />
          {!isFinalizada && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
              <button
                className='wf-btn wf-btn--secondary wf-btn--sm'
                onClick={salvarJustificativa}
                disabled={salvando || !justificativa.trim() || !justificativaAlterada}
              >
                {salvando ? 'Salvando...' : 'Salvar justificativa'}
              </button>
              {justificativaAlterada && (
                <span style={{ fontSize: 11, color: '#ffc700' }}>● Alterações não salvas</span>
              )}
              {item.justificativaObrigatoria && justificativa.trim() && !justificativaAlterada && (
                <span style={{ fontSize: 11, color: '#50cd89' }}>✓ Justificativa registrada — envio para aprovação liberado</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Botão "Enviar para Aprovação" — trava SOX */}
      {!isFinalizada && item.status !== 'AGUARDANDO_APROVACAO' && (
        <div style={{ padding: '16px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <button
              className='wf-btn wf-btn--primary'
              disabled={!podeEnviarAprovacao || justificativaAlterada}
              onClick={() => history.push(`/workflow-aprovacao/conciliacoes/${item.id}`)}
              title={!podeEnviarAprovacao ? 'Salve a justificativa técnica para liberar o envio para aprovação' : undefined}
            >
              Enviar para Aprovação
            </button>
            {!podeEnviarAprovacao && (
              <span style={{ fontSize: 12, color: '#f64e60' }}>
                🔒 Trava SOX: preencha e salve a justificativa para liberar
              </span>
            )}
            {justificativaAlterada && podeEnviarAprovacao && (
              <span style={{ fontSize: 12, color: '#ffc700' }}>
                Salve a justificativa antes de enviar para aprovação
              </span>
            )}
          </div>
        </div>
      )}

      {/* Gestor de Evidências */}
      <div className='wf-capa-block'>
        <div className='wf-capa-block__title' style={{ background: '#005e96' }}>Gestor de Evidências — Documentos de Suporte</div>
        <div style={{ padding: 16 }}>
          {!isFinalizada && <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
            <input value={novaEvidencia} onChange={e => setNovaEvidencia(e.target.value)} placeholder='Nome do arquivo ou link de evidência...' style={{ flex: 1, minHeight: 38, padding: '8px 12px', border: '1px solid #dfe3ea', borderRadius: 8, fontSize: 12 }} />
            <button className='wf-btn wf-btn--primary wf-btn--sm' onClick={adicionarEvidencia}>Adicionar</button>
          </div>}
          <div className='wf-table-wrap'><table className='wf-table'>
            <thead><tr><th>Arquivo</th><th>Tipo</th><th>Tamanho</th><th>Data de upload</th><th>Usuário</th></tr></thead>
            <tbody>{evidencias.map(e => <tr key={e.id}>
              <td><strong>{e.nome}</strong></td><td>{e.tipo}</td><td>{e.tamanho}</td><td>{e.dataUpload}</td><td>{e.usuario}</td>
            </tr>)}</tbody>
          </table></div>
        </div>
      </div>

      {/* Ajustes */}
      <div className='wf-capa-block'>
        <div className='wf-capa-block__title wf-capa-block__title--success'>Ajustes e Pendências (Créditos)</div>
        <div className='wf-capa-lancamentos'>
          {ajustes.length === 0 ? <p className='wf-count'>Nenhum lançamento encontrado.</p> : ajustes.map(l => <LancamentoRow item={l} key={l.id} />)}
        </div>
      </div>

      {/* Composição */}
      <div className='wf-capa-block'>
        <div className='wf-capa-block__title wf-capa-block__title--info'>Composição (Débitos)</div>
        <div className='wf-capa-lancamentos'>
          {composicao.length === 0 ? <p className='wf-count'>Nenhum lançamento encontrado.</p> : composicao.map(l => <LancamentoRow item={l} key={l.id} />)}
        </div>
      </div>

      {/* Log de Workflow */}
      <div className='wf-capa-block'>
        <div className='wf-capa-block__title wf-capa-block__title--dark'>Log de Workflow SOX — Trilha de Aprovações</div>
        <div style={{ padding: 16 }}>
          <div className='wf-approvers'>
            {item.steps.filter(s => s.status !== 'NAO_INICIADA').map(step => <div key={step.id} className={`wf-approver wf-approver--${step.status === 'CONCLUIDA' ? 'done' : step.status === 'REJEITADA' ? 'rejected' : 'active'}`}>
              <div className='wf-approver__icon'>•</div>
              <div className='wf-approver__body'>
                <strong>N{step.id} — {step.cargo}</strong>
                <span>{step.responsavel} &nbsp;|&nbsp; {step.status === 'CONCLUIDA' ? step.concluidoEm ? new Date(step.concluidoEm).toLocaleString('pt-BR') : '' : step.status === 'REJEITADA' ? 'Devolvido' : 'Aguardando'}</span>
                {step.autenticador && <code>Auth: {step.autenticador}</code>}
                {step.comentario && <p>{step.comentario}</p>}
              </div>
            </div>)}
          </div>
        </div>
      </div>

    </div>
  </div>;
};

export default CapaContaContabil;
